import { cn } from "@/lib/utils";
import {
  AddressFormData,
  CountriesAndLanguages,
  CreateAddressBody,
  dynamicAddressSchema,
  CreateCustomerAddressResponse,
  CustomerAddress,
  CustomerAddressResponse,
} from "@/networking/addresses.api";
import {
  customerAddressesQueryKey,
  useCreateAddressMutation,
  useDeleteAddressMutation,
  useUpdateAddressMutation,
} from "@/hooks/useCustomerAddresses";
import useJwt from "@/components/checkout/hooks/useJwt";
import { z } from "zod";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import AddressDialog from "./address-dialog";
import DeleteDialog from "./confirm-action-dialog";
import EllipsesDropdown from "./ellipses-dropdown";
import useAutocomplete from "./hooks/useAutocomplete";
import useCountryStates from "../management/hooks/useCountryStates";
import RadioInputBar from "./radio-input-bar";
import { CartResponse } from "@/networking/carts.api";
import useSetDefaultAddress from "./hooks/useSetDefaultAddress";
import DropZone from "../drop-zones/drop-zone";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { isApiError } from "@/lib/api-helpers";
import {
  useAddressFields,
  useDynamicAddressResolver,
} from "@/hooks/useAddressFields";
import { resolveCountryName } from "@/lib/address-country-selection";

export default function CustomerShippingAddresses({
  customerAddresses,
  customerAddressesLoading,
  selectedShippingAddressId,
  handleSelect,
  cartToken,
  // jwt prop kept for caller compatibility; auth is now read inside the
  // useCustomerAddresses hooks via useJwt(), so this is unused locally.
  jwt: _jwt,
  countries,
  networkError,
  cartCountry,
  cartIso,
  query,
  isAddressMutationPending = false,
}: {
  customerAddresses: CustomerAddress[];
  customerAddressesLoading: boolean;
  selectedShippingAddressId: number | null;
  handleSelect: (address: CustomerAddress) => void | Promise<void>;
  cartToken: string;
  jwt: string;
  countries: CountriesAndLanguages["countries"];
  networkError: string | undefined;
  paymentMethodsIncludes: string | null;
  cartCountry?: string;
  cartIso?: string;
  query?: UseQueryResult<CartResponse | null>;
  isAddressMutationPending?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { subdomain } = query?.data?.cart?.company || {};
  const { jwt: jwtFromHook } = useJwt();
  // Mirror the canonical key from useCustomerAddressesQuery so optimistic
  // writes target the same cache entry as the parent query in logged-in-
  // checkout.tsx.
  const customerAddressesKey = customerAddressesQueryKey(jwtFromHook);
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] =
    useState<CustomerAddress | null>(null);
  const [deleteAddressError, setDeleteAddressError] = useState<
    string | undefined
  >(undefined);
  const [saveAddressError, setSaveAddressError] = useState<string | undefined>(
    undefined,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { handleCountryChange, stateOptions, selectedCountry } =
    useCountryStates(countries);

  const { addressFields } = useAddressFields(selectedCountry);

  const resolver = useDynamicAddressResolver<AddressFormData>(
    addressFields,
    (fields) =>
      dynamicAddressSchema(fields, {
        nameRequired: t("name_is_required"),
        addressLine1Required: t("address_line_1_is_required"),
        cityRequired: t("city_is_required"),
        stateRequired: t("state_is_required"),
        postalCodeRequired: t("postal_code_min_characters"),
        countryRequired: t("country_is_required"),
        phoneRequired: t("address_phone_number_is_required"),
      }).extend({
        default: z.boolean().optional(),
      }),
  );

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<AddressFormData>({
      resolver,
      defaultValues: {
        country: cartCountry || "United States",
        country_code: cartIso || selectedCountry,
        default: false,
      },
    });

  useEffect(() => {
    if (selectedAddress) {
      const addressCountryCode =
        selectedAddress.country_code ?? selectedCountry;
      const addressCountryName = resolveCountryName(
        countries,
        addressCountryCode,
        cartCountry || "United States",
      );
      reset({
        country: addressCountryName,
        country_code: addressCountryCode,
        name: selectedAddress.name ?? "",
        address1: selectedAddress.address1 ?? "",
        address2: selectedAddress.address2 ?? "",
        city: selectedAddress.city ?? "",
        state: selectedAddress.state ?? "",
        postal_code: selectedAddress.postal_code ?? "",
        phone: selectedAddress.phone ?? "",
        default: selectedAddress.default ?? false,
      });
      if (selectedAddress.country_code) {
        handleCountryChange(selectedAddress.country_code);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  const formCountry = watch("country");

  const {
    renderAutocompleteOptions,
    getAutocompleteOptions,
    handleOptionClick,
  } = useAutocomplete(
    setValue,
    "city",
    "state",
    "postal_code",
    "address1",
    stateOptions,
  );

  const createAddressMutation = useCreateAddressMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: customerAddressesKey });
    },
    onSuccess: async (response: CreateCustomerAddressResponse) => {
      handleSelect({
        ...response.address,
        subdivision_code: response.address.state,
      });
      await queryClient.refetchQueries({ queryKey: customerAddressesKey });
      setSaveAddressError(undefined);
    },
    onError: () => {
      setSaveAddressError(t("unable_to_add_your_address"));
    },
  });

  const updateAddressMutation = useUpdateAddressMutation({
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: customerAddressesKey });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: customerAddressesKey });
      setSaveAddressError(undefined);
    },
    onError: (error: unknown) => {
      if (isApiError(error) && error.data?.errors?.address?.[0]) {
        setSaveAddressError(error.data.errors.address[0]);
      } else {
        setSaveAddressError(t("unable_to_update_your_address"));
      }
    },
  });

  const deleteAddressMutation = useDeleteAddressMutation<{
    previousAddresses: CustomerAddressResponse | undefined;
  }>({
    onMutate: async (address_id) => {
      await queryClient.cancelQueries({ queryKey: customerAddressesKey });

      const previousAddresses =
        queryClient.getQueryData<CustomerAddressResponse>(customerAddressesKey);

      queryClient.setQueryData<CustomerAddressResponse>(
        customerAddressesKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            addresses: old.addresses.filter(
              (address) => address.id !== parseInt(address_id, 10),
            ),
          };
        },
      );
      return { previousAddresses };
    },
    onError: (error: unknown, _variables, context) => {
      if (isApiError(error) && error.data?.errors?.address?.[0]) {
        setDeleteAddressError(error.data.errors.address[0]);
      } else {
        setDeleteAddressError(t("unable_to_delete_this_address"));
      }
      if (context?.previousAddresses) {
        queryClient.setQueryData(
          customerAddressesKey,
          context.previousAddresses,
        );
      }
    },
    onSuccess: async () => {
      setDeleteAddressError(undefined);
      handleClose();
      setOpenDeleteDialog(false);
      await Promise.all([
        queryClient.refetchQueries({ queryKey: customerAddressesKey }),
        queryClient.refetchQueries({ queryKey: ["cart", cartToken] }),
      ]);
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    const isFirstCustomerAddress = customerAddresses.length === 0;
    try {
      setIsSubmitting(true);
      const body: CreateAddressBody = {
        address: {
          address1: data.address1,
          address2: data.address2 ?? "",
          city: data.city,
          state: data.state,
          postal_code: data.postal_code,
          phone: data.phone || undefined,
          country_code: selectedCountry,
          name: data.name,
          default: data.default || isFirstCustomerAddress ? true : false,
        },
      };
      if (selectedAddress) {
        await updateAddressMutation.mutateAsync({
          body,
          addressId: selectedAddress.id.toString(),
        });
      } else {
        await createAddressMutation.mutateAsync(body);
      }
      handleClose();
    } catch (error) {
      console.error("Error submitting address", error);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDeleteClick = (address: CustomerAddress) => {
    setDeleteAddressError(undefined);
    requestAnimationFrame(() => {
      setOpenDeleteDialog(true);
    });
    setSelectedAddress(address);
  };

  const handleEditClick = (address: CustomerAddress) => {
    setSaveAddressError(undefined);
    requestAnimationFrame(() => {
      setOpenAddressDialog(true);
    });
    setSelectedAddress(address);
  };

  const handleDeleteAddress = () => {
    if (selectedAddress) {
      deleteAddressMutation.mutate(selectedAddress.id.toString());
    }
  };

  const handleAddClick = () => {
    setSaveAddressError(undefined);
    setSelectedAddress(null);
    reset({
      country: cartCountry || "United States",
      country_code: cartIso || selectedCountry,
      name: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postal_code: "",
      phone: "",
      default: false,
    });
    if (cartIso) {
      handleCountryChange(cartIso);
    }
    setOpenAddressDialog(true);
  };

  const handleClose = () => {
    setOpenAddressDialog(false);
    setSelectedAddress(null);
    reset({
      country: cartCountry || "United States",
      country_code: cartIso || selectedCountry,
      name: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postal_code: "",
      phone: "",
    });
    if (cartIso) {
      handleCountryChange(cartIso);
    }
  };

  const [optimisticSelectedAddressId, setOptimisticSelectedAddressId] =
    useState<number | null>(null);

  useEffect(() => {
    if (
      optimisticSelectedAddressId !== null &&
      selectedShippingAddressId === optimisticSelectedAddressId
    ) {
      setOptimisticSelectedAddressId(null);
    }
  }, [optimisticSelectedAddressId, selectedShippingAddressId]);

  const effectiveSelectedAddressId =
    optimisticSelectedAddressId ?? selectedShippingAddressId;

  const handleSelectAddress = async (address: CustomerAddress) => {
    setOptimisticSelectedAddressId(address.id);
    try {
      await handleSelect(address);
    } catch {
      // Parent mutation surfaces the specific reason via addressError
      // (rendered in the checkout error section). Clear optimistic so
      // the radio snaps back to the cart's actual ship_to — otherwise
      // the UI would lie about which address is selected vs what Pay
      // Now would ship to.
      setOptimisticSelectedAddressId(null);
    }
  };

  const handleMakeDefaultAddress = (address: CustomerAddress) => {
    updateAddressMutation.mutate({
      addressId: address.id.toString(),
      body: {
        address: {
          name: address.name ?? "",
          address1: address.address1,
          address2: address.address2 ?? null,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
          phone: address.phone ?? undefined,
          country_code: address.country_code ?? "",
          default: true,
        },
      },
    });
  };

  useSetDefaultAddress(
    customerAddresses,
    query?.data?.cart?.country?.iso,
    selectedShippingAddressId,
    handleSelect,
  );

  // Default address rendered first. Visual only; underlying data unchanged.
  const displayedAddresses = useMemo(() => {
    if (!customerAddresses?.length) return customerAddresses;
    const defaultIdx = customerAddresses.findIndex((a) => a.default);
    if (defaultIdx <= 0) return customerAddresses;
    const defaultAddress = customerAddresses[defaultIdx];
    if (!defaultAddress) return customerAddresses;
    return [
      defaultAddress,
      ...customerAddresses.filter((_, i) => i !== defaultIdx),
    ];
  }, [customerAddresses]);

  const displayAddress = customerAddresses?.find(
    (address) => address.id === effectiveSelectedAddressId,
  );

  const isLoading = customerAddressesLoading || query?.isLoading;

  return (
    <div className="w-full border-b border-gray-300 pb-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="w-full"
      >
        <AccordionItem value="shipping-addresses" className="border-b-0">
          <AccordionTrigger
            data-testid="toggle-shipping-addresses"
            className="mt-4 mb-1 flex w-full items-center justify-between py-1 text-left hover:no-underline"
          >
            <span className="text-sm text-gray-400">{t("ship_to")}</span>
          </AccordionTrigger>

          {networkError && (
            <div
              className="text-sm text-red-500"
              data-testid="shipping-address-error-inline"
            >
              {networkError}
            </div>
          )}

          {isLoading && (
            <div className="flex flex-col space-y-2">
              <div className="h-6 w-full animate-pulse rounded bg-gray-200"></div>
            </div>
          )}

          {!accordionValue && displayAddress && !isLoading && (
            <div
              className="flex w-full cursor-pointer flex-col"
              onClick={() => setAccordionValue("shipping-addresses")}
            >
              <div className="flex items-center text-sm font-medium text-gray-900">
                {displayAddress.name}, {displayAddress.address1}
              </div>
              <div className="text-sm text-gray-500">
                {displayAddress.city}, {displayAddress.state}{" "}
                {displayAddress.postal_code}
              </div>
              {displayAddress.phone && (
                <div className="text-sm text-gray-500">
                  {displayAddress.phone}
                </div>
              )}
            </div>
          )}

          <AccordionContent className="px-0.5 pt-2">
            {!customerAddresses.length ? (
              <div className="mt-2 mb-1 text-sm text-gray-400">
                {t("no_saved_addresses_found")}
              </div>
            ) : (
              <div
                className="mt-2 flex flex-col justify-between"
                data-testid="shipping-address-options"
              >
                {displayedAddresses?.map((address, index) => {
                  const isLastItem = index === displayedAddresses.length - 1;
                  const isFirstItem = index === 0;
                  return (
                    <RadioInputBar
                      key={address.id}
                      onSelect={() => handleSelectAddress(address)}
                      leftContent={
                        <div className="flex flex-col">
                          <div className="text-sm text-gray-900">
                            {address.name} <br />
                            {address.address1} <br />
                            {address.city}, {address.state}{" "}
                            {address.postal_code}
                            {address.phone && (
                              <>
                                <br />
                                {address.phone}
                              </>
                            )}
                          </div>
                          {address.default && (
                            <p className="mt-1 inline-flex w-fit items-center justify-center rounded bg-gray-500 px-2 py-0.5 text-xs font-medium text-white">
                              {t("default")}
                            </p>
                          )}
                        </div>
                      }
                      isSelected={effectiveSelectedAddressId === address.id}
                      name={address.name ?? ""}
                      rightContent={
                        <EllipsesDropdown
                          onDelete={() => handleDeleteClick(address)}
                          onEdit={() => handleEditClick(address)}
                          editLabel={t("edit")}
                          deleteLabel={t("delete")}
                          onMakeDefault={
                            address.default
                              ? undefined
                              : () => handleMakeDefaultAddress(address)
                          }
                          makeDefaultLabel={t("set_as_default")}
                          disabled={isAddressMutationPending}
                        />
                      }
                      className={cn(
                        "cursor-pointer items-center bg-gray-100/40 p-3",
                        {
                          "bg-white opacity-100":
                            effectiveSelectedAddressId === address.id,
                        },
                        isLastItem && "rounded-b",
                        isFirstItem && "rounded-t",
                      )}
                    />
                  );
                })}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <button
        className="mt-3 text-left text-sm text-gray-900"
        onClick={handleAddClick}
      >
        + {t("add_an_address")}
      </button>
      <DropZone
        zone="below_shipping_address"
        token={cartToken}
        subdomain={subdomain ?? null}
        page="checkout"
        companyId={query?.data?.cart?.company?.id}
      />
      <DeleteDialog
        title={t("delete_address")}
        description={t("delete_address_message")}
        onAction={handleDeleteAddress}
        actionText={t("delete")}
        openDialog={openDeleteDialog}
        setOpenDialog={setOpenDeleteDialog}
        errorMsg={deleteAddressError}
        isLoading={deleteAddressMutation.isPending}
      />
      <AddressDialog
        isOpen={openAddressDialog}
        countries={countries || []}
        control={control}
        setValue={setValue}
        onSubmit={onSubmit}
        handleClose={handleClose}
        renderAutocompleteOptions={renderAutocompleteOptions}
        getAutocompleteOptions={getAutocompleteOptions}
        handleOptionClick={handleOptionClick}
        formCountry={formCountry}
        stateOptions={stateOptions}
        addressFields={addressFields}
        handleCountryChange={handleCountryChange}
        errorMsg={saveAddressError}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
